import { useState, useEffect, useRef } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import wosImg from "../assets/wos.png";
import cite from "../assets/cite.png";
import facebook from "../assets/share/facebook-square.svg";
import twitter from "../assets/share/twitter-square.svg";
import linkedin from "../assets/share/linkedin-square.svg";
import reddit from "../assets/share/reddit-square.svg";
import { Cite } from "@citation-js/core";
import "@citation-js/plugin-doi"; // DOI 支持
import "@citation-js/plugin-bibtex"; // BibTeX 输出
import "@citation-js/plugin-ris"; // RIS 输出

const ArticleCard = ({ article }) => {
  const articleData = article;
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isCiteMenuOpen, setIsCiteMenuOpen] = useState(false); //控制引用菜单显示状态
  const abstractRef = useRef(null);
  const citeMenuRef = useRef(null); // 添加引用
  const citeButtonRef = useRef(null); // 添加引用

  const textStyle = {
    color: "#000",
    lineHeight: "1.6",
    textAlign: "justify",
    margin: 0,
    overflow: "hidden",
    transition: "all 0.3s ease",
    display: isExpanded ? "block" : "-webkit-box",
    WebkitLineClamp: isExpanded ? "unset" : 3,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
  };

  const toggleBtnStyle = {
    textAlign: "end",
    cursor: "pointer",
    padding: "4px 12px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#003366",
    borderColor: "#003366",
    marginTop: "4px",
    "&:hover": {
      backgroundColor: "#003366",
      color: "#fff",
    },
  };

  useEffect(() => {
    if (abstractRef.current && articleData?.abstract) {
      const { scrollHeight, clientHeight } = abstractRef.current;
      setIsOverflow(scrollHeight > clientHeight);
    }
  }, [articleData?.abstract, isExpanded]);

  const toggleExpand = () => setIsExpanded(!isExpanded);
  useEffect(() => {
    const el = abstractRef.current;
    if (!el) return;

    // 计算 3 行的高度：line-height(1.6) × 3
    const threeLineHeight = 1.6 * 3 * parseFloat(getComputedStyle(el).fontSize);

    // 判断内容是否超出
    if (el.scrollHeight > threeLineHeight + 5) {
      setIsOverflowed(true);
    } else {
      setIsOverflowed(false);
    }
  }, [articleData.abstract]);

  // 导出文件函数
  const downloadFile = (filename, content) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCitation = (format, articleData) => {
    if (!articleData) return;

    const authors = articleData.authors
      ? articleData.authors.map((a) => `${a.given} ${a.family}`).join(", ")
      : "";

    let content = "";
    let filename = "";

    switch (format) {
      case "RIS":
        const [startPage, endPage] = (articleData.page || "").split("-");
        content = `TY  - JOUR
        T1  - ${articleData.title}
        AU  - ${articleData.authors
          .map((a) => `${a.family}, ${a.given}`)
          .join("\nAU  - ")}
        JO  - ${articleData.jcr || ""}
        VL  - ${articleData.volume || ""}
        IS  - ${articleData.issued || ""}
        SP  - ${startPage || ""}
        EP  - ${endPage || ""}
        PY  - ${articleData.year || ""}
        DA  - ${new Date(articleData.published_time)
          .toISOString()
          .slice(0, 10)
          .replace(/-/g, "/")}
        SN  - ${articleData.issn || ""}
        DO  - https://doi.org/${articleData.doi || ""}
        UR  - ${articleData.url || ""}
        ER  -`
          .split("\n")
          .map((line) => line.trim())
          .join("\n");
        filename = `${articleData.doi || "citation"}.ris`;
        break;

      case "BibTeX":
        const firstPage = (articleData.page || "").split("-")[0];
        content = `@article{${
          articleData.authors[0]?.family.toUpperCase() || "AUTHOR"
        }${articleData.year || ""}${firstPage || ""},
          title = {${articleData.title}},
          journal = {${articleData.jcr || ""}},
          volume = {${articleData.volume || ""}},
          number = {${articleData.issued || ""}},
          pages = {${articleData.page || ""}},
          year = {${articleData.year || ""}},
          issn = {${articleData.issn || ""}},
          doi = {https://doi.org/${encodeURIComponent(articleData.doi)}},
          url = {${articleData.url || ""}},
          author = {${authors}}
          }`
          .split("\n")
          .map((line) => line.trim())
          .join("\n");
        filename = `${articleData.doi || "citation"}.bib`;
        break;

      case "TXT":
        content = `${authors},
        ${articleData.title},
        ${articleData.jcr || ""},
        Volume ${articleData.volume || ""}, Issue ${articleData.issued || ""},
        ${articleData.year || ""},
        Pages ${articleData.page || ""},
        ISSN ${articleData.issn || ""},
        https://doi.org/${encodeURIComponent(articleData.doi)}
        `
          .split("\n")
          .map((line) => line.trim())
          .join("\n");
        filename = `${articleData.doi || "citation"}.txt`;
        break;
      case "EndNote":
        content = `%0 Journal Article
        %A ${articleData.authors
          .map((a) => `${a.family}, ${a.given}`)
          .join("\n%A ")}
        %T ${articleData.title}
        %J ${articleData.jcr || ""}
        %V ${articleData.volume || ""}
        %N ${articleData.issued || ""}
        %P ${articleData.page || ""}
        %D ${articleData.year || ""}
        %8 ${new Date(articleData.published_time).toISOString().slice(0, 10)}
        %IS ${articleData.issn || ""}
        %U https://doi.org/${articleData.doi || ""}
        %X ${articleData.abstract || ""}
        `
          .split("\n")
          .map((line) => line.trim())
          .join("\n");
        filename = `${articleData.doi || "citation"}.enw`;
        break;

      default:
        return;
    }

    downloadFile(filename, content);
  };

  // 切换引用菜单显示/隐藏
  const ontoggleCiteMenu = () => {
    setIsCiteMenuOpen(!isCiteMenuOpen);
  };

  //点击外部关闭引用菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 使用 ref 而不是 querySelector 来确保操作正确的元素
      if (
        isCiteMenuOpen &&
        citeMenuRef.current &&
        !citeMenuRef.current.contains(event.target) &&
        citeButtonRef.current &&
        !citeButtonRef.current.contains(event.target)
      ) {
        setIsCiteMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCiteMenuOpen]);

  // 清理摘要文本函数
  const cleanAbstract = (xml = "") => {
    if (!xml) return "";
  
    let text = xml;
  
    // 1. 移除 jats: 前缀
    text = text.replace(/<jats:/g, "<").replace(/<\/jats:/g, "</");
  
    // 2. 匹配第一个 <sec>
    const firstSecMatch = text.match(/<sec[^>]*>([\s\S]*?)<\/sec>/i);
    if (!firstSecMatch) return "";
  
    const firstSecContent = firstSecMatch[1];
  
    // 3. 抓第一个 <p>
    const firstPMatch = firstSecContent.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    if (!firstPMatch) return "";
  
    let firstContent = firstPMatch[1].trim();
  
    // 4. ⭐删除被转义的标签：&lt;xxx&gt;、&lt;/xxx&gt;
    firstContent = firstContent.replace(/&lt;\/?[^&]+?&gt;/g, "");
  
    // 5. 返回 span
    const result = `<span style='color:#000'>${firstContent}</span>`;
  
    return result;
  };
  

  const cleanTitle = (html = "") => {
    if (!html) return "";

    // 1. 统一把开标签改成 <span>
    let text = html.replace(/<[^/][^>]*>/g, "<span style='color:#003366'>");

    // 2. 把闭标签改成 </span>
    text = text.replace(/<\/[^>]+>/g, "</span>");

    return text;
  };

  return (
    <div style={{ position: "relative" }}>
      <article
        className="card mb-20 article-card-home"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "visible",
          position: "relative",
          zIndex: 0,
        }}
      >
        {/* 头部 */}
        <div className="flex justify-between">
          <div className="d-flex">
            <svg
              t="1762311984213"
              className="icon"
              viewBox="0 0 1024 1024"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              p-id="8299"
              width="24"
              height="24"
            >
              <path
                d="M780.190476 121.904762a73.142857 73.142857 0 0 1 73.142857 73.142857v633.904762a73.142857 73.142857 0 0 1-73.142857 73.142857H292.571429a73.142857 73.142857 0 0 1-73.142858-73.142857v-48.761905H170.666667v-73.142857h170.666666v73.142857h-48.761904v48.761905h487.619047V195.047619H292.571429v48.761905h48.761904v73.142857H170.666667v-73.142857h48.761904V195.047619a73.142857 73.142857 0 0 1 73.142858-73.142857h487.619047z m-64.682666 121.904762v536.380952h-73.142858V243.809524h73.142858zM292.571429 365.714286v292.571428H219.428571V365.714286h73.142858z"
                p-id="8300"
                fill="#003366"
              ></path>
            </svg>
            <span>{articleData && articleData.jcr}</span>
          </div>
          <div>
            <a
              className="articles-cite"
              target="_blank"
              href={`https://scholar.google.com/scholar?q=${encodeURIComponent(
                articleData.doi
              )}`}
              style={{
                "--dot-color": articleData.cite > 0 ? "#1AC604" : "#F7931E",
              }}
            >
              <img style={{ width: "18px" }} src={cite} />{" "}
              {articleData.cite ? articleData.cite : ""}
            </a>
          </div>
        </div>
        {/* 标题 */}
        {articleData && articleData.title && (
          <h3
            style={{
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#003366",
              margin: "0px",
            }}
          >
            <a
              href={`https://doi.org/${encodeURIComponent(articleData.doi)}`}
              target="_blank"
              dangerouslySetInnerHTML={{
                __html: cleanTitle(articleData.title),
              }}
            ></a>
          </h3>
        )}
        {/* 作者列表 */}
        {articleData && articleData.authors && (
          <div style={{ color: "#666" }}>
            {articleData.authors.map((name, index) => (
              <span key={index}>
                {name.ORCID && (
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      paddingRight: "14px",
                      fontSize: "14px",
                    }}
                  >
                    {name.given} {name.family}
                    {name.sequence === "first" ? "*" : ""}
                    <a
                      href={name.ORCID || ""}
                      target={name.ORCID ? "_blank" : undefined}
                      rel={name.ORCID ? "noreferrer" : undefined}
                      className={name.ORCID ? "" : `orcid-id-link`}
                    >
                      <svg
                        width="10px"
                        height="10px"
                        viewBox="0 0 72 72"
                        version="1.1"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "2px",
                          display: "inline-block",
                          lineHeight: 0,
                        }}
                      >
                        <g
                          id="Symbols"
                          stroke="none"
                          strokeWidth="1"
                          fill="none"
                          fillRule="evenodd"
                        >
                          <g
                            id="hero"
                            transform="translate(-924.000000, -72.000000)"
                            fillRule="nonzero"
                          >
                            <g id="Group-4">
                              <g
                                id="vector_iD_icon"
                                transform="translate(924.000000, 72.000000)"
                              >
                                <path
                                  d="M72,36 C72,55.884375 55.884375,72 36,72 C16.115625,72 0,55.884375 0,36 C0,16.115625 16.115625,0 36,0 C55.884375,0 72,16.115625 72,36 Z"
                                  id="Path"
                                  fill="#A6CE39"
                                ></path>
                                <g
                                  id="Group"
                                  transform="translate(18.868966, 12.910345)"
                                  fill="#FFFFFF"
                                >
                                  <polygon
                                    id="Path"
                                    points="5.03734929 39.1250878 0.695429861 39.1250878 0.695429861 9.14431787 5.03734929 9.14431787 5.03734929 22.6930505 5.03734929 39.1250878"
                                  ></polygon>
                                  <path
                                    d="M11.409257,9.14431787 L23.1380784,9.14431787 C34.303014,9.14431787 39.2088191,17.0664074 39.2088191,24.1486995 C39.2088191,31.846843 33.1470485,39.1530811 23.1944669,39.1530811 L11.409257,39.1530811 L11.409257,9.14431787 Z M15.7511765,35.2620194 L22.6587756,35.2620194 C32.49858,35.2620194 34.7541226,27.8438084 34.7541226,24.1486995 C34.7541226,18.1301509 30.8915059,13.0353795 22.4332213,13.0353795 L15.7511765,13.0353795 L15.7511765,35.2620194 Z"
                                    id="Shape"
                                  ></path>
                                  <path
                                    d="M5.71401206,2.90182329 C5.71401206,4.441452 4.44526937,5.72914146 2.86638958,5.72914146 C1.28750978,5.72914146 0.0187670918,4.441452 0.0187670918,2.90182329 C0.0187670918,1.33420133 1.28750978,0.0745051096 2.86638958,0.0745051096 C4.44526937,0.0745051096 5.71401206,1.36219458 5.71401206,2.90182329 Z"
                                    id="Path"
                                  ></path>
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                    </a>
                    {index < articleData.authors.length - 1 ? ", " : ""}
                  </span>
                )}

                {name.ORCID === null && (
                  <span style={{ fontSize: "14px" }}>
                    {name.given} {name.family}
                    {name.sequence === "first" ? "*" : ""}
                    {index < articleData.authors.length - 1 ? ", " : ""}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
        {/* 可视化摘要 无数据未渲染*/}
        {articleData &&
          articleData.abstract &&
          articleData.abstract.visual_abstract && (
            <div style={{ overflow: "auto" }}>
              <img
                src={articleData.abstract.visual_abstract.image_url.replace(
                  "..",
                  ""
                )}
                alt="Article visual"
                style={{
                  width: "305px",
                  height: "auto",
                  objectFit: "cover",
                  borderRadius: "8px",
                  float: "left",
                  marginRight: "20px",
                  marginBottom: "15px",
                  shapeOutside: "margin-box",
                }}
              />
              <p
                style={{
                  color: "#555",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: articleData.abstract.visual_abstract.text,
                }}
              ></p>
              <div style={{ clear: "both" }}></div>
            </div>
          )}
        {/* 摘要 */}
        {cleanAbstract(articleData.abstract)?.trim() ? (
          <>
            <p
              ref={abstractRef}
              style={textStyle}
              dangerouslySetInnerHTML={{
                __html: cleanAbstract(articleData.abstract),
              }}
            />
            {isOverflowed && (
              <div style={{ textAlign: "end" }}>
                <span style={toggleBtnStyle} onClick={toggleExpand}>
                  <a>{isExpanded ? "Hide" : "Show More"}</a>
                </span>
              </div>
            )}
          </>
        ) : null}
        {/* 下载链接 */}
        {articleData && (
          <div
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
          >
            {/* PDF */}
            {articleData.pdf && (
              <>
                <div>
                  <a href={articleData.wos_id} target="_blank" rel="noreferrer">
                    <svg
                      t="1755829841268"
                      className="icon"
                      viewBox="0 0 1024 1024"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      p-id="927"
                      width="24"
                      height="24"
                    >
                      <path
                        d="M590.222222 79.644444L859.022222 347.943822V881.777778c0 37.700267-30.5664 68.266667-68.266666 68.266666H233.244444c-37.700267 0-68.266667-30.5664-68.266666-68.266666V147.911111c0-37.700267 30.5664-68.266667 68.266666-68.266667h356.977778z m-11.764622 28.444445H233.244444a39.822222 39.822222 0 0 0-39.799466 38.456889L193.422222 147.911111v733.866667a39.822222 39.822222 0 0 0 38.456889 39.799466L233.244444 921.6h557.511112a39.822222 39.822222 0 0 0 39.799466-38.456889L830.577778 881.777778V359.742578L578.4576 108.088889z"
                        fill="#003366"
                        p-id="928"
                      ></path>
                      <path
                        d="M854.755556 376.337067h-215.1424c-45.528178 0-82.551467-36.431644-83.5072-81.737956l-0.017067-1.792V79.644444h28.444444v213.162667c0 29.895111 23.819378 54.232178 53.515378 55.062756l1.564445 0.022755h215.1424v28.444445z"
                        fill="#003366"
                        p-id="929"
                      ></path>
                      <path
                        d="M56.888889 494.933333m28.444444 0l853.333334 0q28.444444 0 28.444444 28.444445l0 284.444444q0 28.444444-28.444444 28.444445l-853.333334 0q-28.444444 0-28.444444-28.444445l0-284.444444q0-28.444444 28.444444-28.444445Z"
                        fill="#003366"
                        p-id="930"
                      ></path>
                      <path
                        d="M318.435556 762.311111v-63.232h22.272c40.448 0 75.264-19.968 75.264-65.28 0-47.104-34.56-62.208-76.288-62.208H272.611556V762.311111h45.824z m19.968-99.328h-19.968v-55.04h18.688c22.016 0 34.304 6.656 34.304 25.856 0 18.688-10.752 29.184-33.024 29.184zM505.827556 762.311111c55.552 0 93.952-29.952 93.952-96.256 0-66.304-38.4-94.464-96.512-94.464h-54.784V762.311111h57.344z m-5.376-36.864h-6.144v-117.248h6.144c30.72 0 52.48 12.544 52.48 57.856s-21.76 59.392-52.48 59.392z m180.48 36.864v-72.704h65.024v-38.144h-65.024v-41.728h75.776v-38.144H635.107556V762.311111h45.824z"
                        fill="#FFFFFF"
                        p-id="931"
                      ></path>
                    </svg>
                    <span>Download PDF</span>
                  </a>
                </div>
                <div className="vr" style={{ height: "24px" }} />
              </>
            )}

            {/* WOS */}
            {articleData.wos_id && (
              <>
                <div>
                  <a
                    href={articleData.wos_id}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <img
                      src={wosImg}
                      alt="Web of Science"
                      style={{
                        width: "20px",
                        height: "20px",
                        marginRight: "4px",
                      }}
                    />
                    <span>
                      {articleData.wos_id
                        ? articleData.wos_id.split("/").pop()
                        : ""}
                    </span>
                  </a>
                </div>
                <div className="vr" style={{ height: "24px" }} />
              </>
            )}
            {/* DOI */}
            <div>
              {articleData.doi && (
                <a
                  href={`https://doi.org/${encodeURIComponent(
                    articleData.doi
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  DOI:
                  {articleData.doi}
                </a>
              )}
            </div>
          </div>
        )}
        {/* 展示年月 */}
        {articleData && articleData.published_time && (
          <div className="flex justify-between items-center">
            <div className="mb-15" style={{ color: "#666" }}>
              Published:{" "}
              {new Date(articleData.published_time).toISOString().slice(0, 7)}
            </div>
          </div>
        )}
        {/* 分享与引用 */}
        <div className="flex align-center justify-between gap-10">
          <div className="d-flex align-center gap-10">
            <span>Share:</span>
            <span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=https://doi.org/${encodeURIComponent(
                  articleData.doi
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                <img src={facebook} alt="facebook" />
              </a>
            </span>
            <span>
              <a
                href={`https://x.com/share?text=${encodeURIComponent(
                  articleData.title
                )}&url=https://doi.org/${encodeURIComponent(articleData.doi)}`}
                target="_blank"
                rel="noreferrer"
              >
                <img src={twitter} alt="twitter" />
              </a>
            </span>
            <span>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=https://doi.org/${encodeURIComponent(
                  articleData.doi
                )}&title=${encodeURIComponent(articleData.title)}`}
                target="_blank"
                rel="noreferrer"
              >
                <img src={linkedin} alt="linkedin" />
              </a>
            </span>
            <span>
              <a
                href={`https://reddit.com/submit?url=${encodeURIComponent(
                  articleData.doi
                )}&title=${encodeURIComponent(articleData.title)}`}
                target="_blank"
                rel="noreferrer"
              >
                <img src={reddit} alt="reddit" />
              </a>
            </span>
          </div>
          <div>
            <a
              ref={citeButtonRef}
              onClick={ontoggleCiteMenu}
              style={{ cursor: "pointer" }}
              data-cite-button="true"
            >
              <svg
                t="1763091625610"
                className="icon"
                viewBox="0 0 1211 1024"
                version="1.1"
                p-id="2557"
                width="28.3828125"
                height="16"
              >
                <path
                  d="M1206.243142 309.434182c0.279273 268.567273-142.429091 517.585455-376.180364 656.011636a87.412364 87.412364 0 0 1-92.625454 8.936727 84.712727 84.712727 0 0 1-47.010909-79.127272 84.992 84.992 0 0 1 53.34109-75.124364c102.4-60.229818 184.32-148.945455 235.52-254.882909h-28.392727c-142.987636 0-258.885818-114.129455-258.885818-254.976 0-140.753455 115.898182-254.882909 258.792727-254.882909 143.080727 0 258.978909 114.129455 258.978909 254.882909l-3.444363-0.837818z m-690.362182 0c0 268.288-142.801455 516.840727-376.180364 655.173818a87.412364 87.412364 0 0 1-92.718545 8.936727 84.712727 84.712727 0 0 1-46.917818-79.127272 84.992 84.992 0 0 1 53.341091-75.124364c102.4-60.229818 184.32-148.945455 235.52-254.882909H260.439505C117.451869 564.410182 1.646778 450.280727 1.646778 309.434182 1.646778 168.680727 117.358778 54.551273 260.439505 54.551273c142.987636 0 258.978909 114.129455 258.97891 254.882909h-3.537455z"
                  fill="#003366"
                  p-id="2558"
                ></path>
              </svg>
              Cite
            </a>
          </div>
        </div>
      </article>
      {/* 引用菜单 RIS TXT BibTex EndNote*/}
      <div
        ref={citeMenuRef}
        className="cite-mene"
        style={{
          display: isCiteMenuOpen ? "block" : "none",
          position: "absolute",
          top: "calc(100% - 25px)",
          right: -216,
          zIndex: 10,
          backgroundColor: "#fff",
          borderRadius: "6px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          padding: "16px",
          minWidth: "200px",
        }}
      >
        <div className="cite-menu-content">
          <div
            className="cite-options"
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡
                exportCitation("RIS", articleData);
                setIsCiteMenuOpen(false);
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #003366",
                borderRadius: "4px",
                backgroundColor: "transparent",
                color: "#003366",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
                width: "100%",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#003366";
                e.target.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#003366";
              }}
            >
              Export citation to RIS
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡
                exportCitation("BibTeX", articleData);
                setIsCiteMenuOpen(false);
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #003366",
                borderRadius: "4px",
                backgroundColor: "transparent",
                color: "#003366",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
                width: "100%",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#003366";
                e.target.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#003366";
              }}
            >
              Export citation to BibTeX
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡
                exportCitation("TXT", articleData);
                setIsCiteMenuOpen(false);
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #003366",
                borderRadius: "4px",
                backgroundColor: "transparent",
                color: "#003366",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
                width: "100%",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#003366";
                e.target.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#003366";
              }}
            >
              Export citation to TXT
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡
                exportCitation("EndNote", articleData);
                setIsCiteMenuOpen(false);
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #003366",
                borderRadius: "4px",
                backgroundColor: "transparent",
                color: "#003366",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease",
                width: "100%",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#003366";
                e.target.style.color = "#fff";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#003366";
              }}
            >
              Export citation to EndNote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
